import { amountInWords } from './quotationTemplateTokens';
import type { CompanyProfileLike } from './runningBillInvoice';

// The customer payment receipt: a one-page letterhead document, made into a PDF on download.

export interface ReceiptData {
  company?: CompanyProfileLike | null;
  /** Company logo as a data or https address; anything else is left out. */
  logo?: string;
  receiptNo: string;
  date: string | Date;
  customerName: string;
  customerCode?: string | null;
  quotationNumber?: string;
  /** The invoice or proforma this payment is against. */
  againstNumber?: string;
  milestoneName: string;
  percentage?: number | string;
  narration: string;
  /** In paise. */
  amountPaise: number;
  paymentMode?: string;
  referenceNo?: string;
  /** The account the money was received in, when known. */
  receivedIn?: string;
  generatedAt?: Date;
}

const escapeHtml = (v: unknown): string =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** "bank_transfer" -> "Bank Transfer". */
export function paymentModeLabel(mode?: string): string {
  const text = String(mode ?? '').replace(/[_-]+/g, ' ').trim();
  if (!text) return '—';
  return text.replace(/\b\w/g, (c) => c.toUpperCase());
}

const safeLogo = (src?: string): string => (src && /^(data:image\/|https:\/\/)/i.test(src) ? src : '');

const money = (rupees: number) => rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function buildReceiptHtml(d: ReceiptData): string {
  const c = d.company ?? {};
  const companyName = c.legalName || c.name || '';
  const addr = c.registeredAddress ?? {};
  const addressLine = [addr.line1, addr.line2, [addr.city, addr.state].filter(Boolean).join(', '), addr.pincode]
    .filter(Boolean)
    .join(', ');
  const taxLine = [(c.gstin || c.gst) && `GSTIN: ${c.gstin || c.gst}`, c.pan && `PAN: ${c.pan}`].filter(Boolean).join('  ·  ');
  const contactLine = [c.phone, c.officialEmail || c.email, c.website].filter(Boolean).join('  ·  ');
  const logo = safeLogo(d.logo || c.logo || c.logoUrl);

  const rupees = (Number(d.amountPaise) || 0) / 100;
  const date = new Date(d.date);
  const dateText = Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const generated = (d.generatedAt ?? new Date()).toLocaleString('en-IN');
  const stage = `${d.milestoneName}${d.percentage !== undefined && d.percentage !== '' ? ` (${d.percentage}%)` : ''}`;

  const row = (label: string, value: unknown) =>
    `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value === undefined || value === null || value === '' ? '—' : value)}</td></tr>`;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Receipt ${escapeHtml(d.receiptNo)}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #1f2937; font-size: 13px; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { padding: 40px 44px; }
  .letterhead { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-bottom: 16px; border-bottom: 3px solid #3730a3; }
  .brand { display: flex; align-items: center; gap: 14px; }
  .brand img { max-height: 56px; max-width: 150px; object-fit: contain; }
  .company-name { font-size: 20px; font-weight: 700; color: #1e1b4b; }
  .company-meta { font-size: 11px; color: #4b5563; margin-top: 3px; }
  .doc-title { text-align: right; }
  .doc-title .t { font-size: 26px; font-weight: 800; letter-spacing: 2px; color: #3730a3; }
  .doc-title .n { font-size: 12px; color: #4b5563; margin-top: 4px; }
  .doc-title .n b { color: #111827; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; margin-top: 24px; }
  .card { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
  .card h3 { margin: 0; padding: 8px 14px; background: #eef2ff; color: #3730a3; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 8px 14px; text-align: left; vertical-align: top; border-top: 1px solid #f1f5f9; font-size: 12.5px; }
  th { width: 38%; color: #6b7280; font-weight: 600; }
  td { color: #111827; font-weight: 500; word-break: break-word; }
  .narration { margin-top: 22px; }
  .narration p { margin: 0; padding: 10px 14px; border: 1px solid #e5e7eb; border-radius: 8px; color: #374151; }
  .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; font-weight: 700; margin-bottom: 6px; }
  .amount-box { margin-top: 24px; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 16px 20px; border-radius: 10px; background: #3730a3; color: #fff; }
  .amount-box .lbl { font-size: 12px; letter-spacing: 1px; text-transform: uppercase; opacity: .85; }
  .amount-box .amt { font-size: 28px; font-weight: 800; white-space: nowrap; }
  .words { margin-top: 10px; font-size: 12.5px; color: #374151; }
  .words b { color: #111827; }
  .sign { margin-top: 64px; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
  .sign .note { font-size: 11px; color: #6b7280; max-width: 52%; }
  .sign .box { width: 230px; text-align: center; }
  .sign .box .for { font-size: 11px; color: #4b5563; }
  .sign .box .line { margin-top: 44px; border-top: 1px solid #111827; padding-top: 5px; font-size: 12px; font-weight: 600; }
  .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 10.5px; color: #9ca3af; }
</style>
</head>
<body>
<div class="page">
  <div class="letterhead">
    <div class="brand">
      ${logo ? `<img src="${escapeHtml(logo)}" alt="">` : ''}
      <div>
        <div class="company-name">${escapeHtml(companyName)}</div>
        ${addressLine ? `<div class="company-meta">${escapeHtml(addressLine)}</div>` : ''}
        ${taxLine ? `<div class="company-meta">${escapeHtml(taxLine)}</div>` : ''}
        ${contactLine ? `<div class="company-meta">${escapeHtml(contactLine)}</div>` : ''}
      </div>
    </div>
    <div class="doc-title">
      <div class="t">RECEIPT</div>
      <div class="n">No. <b>${escapeHtml(d.receiptNo)}</b></div>
      <div class="n">Date <b>${escapeHtml(dateText)}</b></div>
    </div>
  </div>

  <div class="grid">
    <div class="card">
      <h3>Received from</h3>
      <table>
        ${row('Customer', d.customerName)}
        ${row('Customer Code', d.customerCode || '—')}
        ${row('Quotation', d.quotationNumber)}
        ${row('Against', d.againstNumber)}
        ${row('Stage', stage)}
      </table>
    </div>
    <div class="card">
      <h3>Payment details</h3>
      <table>
        ${row('Payment Date', dateText)}
        ${row('Payment Mode', paymentModeLabel(d.paymentMode))}
        ${row('Reference No.', d.referenceNo || '—')}
        ${d.receivedIn ? row('Received In', d.receivedIn) : ''}
      </table>
    </div>
  </div>

  <div class="narration">
    <div class="label">Narration</div>
    <p>${escapeHtml(d.narration)}</p>
  </div>

  <div class="amount-box">
    <div class="lbl">Amount Received</div>
    <div class="amt">₹${escapeHtml(money(rupees))}</div>
  </div>
  <div class="words">Amount in words: <b>${escapeHtml(amountInWords(rupees))}</b></div>

  <div class="sign">
    <div class="note">Received with thanks. This receipt acknowledges the payment above against the stated document.</div>
    <div class="box">
      <div class="for">For ${escapeHtml(companyName || 'the company')}</div>
      <div class="line">Authorised Signatory</div>
    </div>
  </div>

  <div class="footer">Generated on ${escapeHtml(generated)}  ·  This is a computer-generated receipt</div>
</div>
</body>
</html>`;
}
